"use client";

import React, { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { AtSymbolIcon, ExclamationCircleIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuthentication } from "@/hooks/useAuthentication";
import { toast } from "sonner";
import { useLocale } from "@/context/LocaleContext";

const COOLDOWN_SECONDS = 30;
const STORAGE_KEY = "forgot_password_cooldown_expires_at";

export default function ForgotPasswordClient() {
  const { t } = useLocale(); 
  usePageTitle(t("ForgotPassword.page_title") || "Forgot Password");

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | undefined>();
  
  const [timeLeft, setTimeLeft] = useState(0); 
  const [isTimerActive, setIsTimerActive] = useState(false); 

  const { forgotPassword, isLoading, error: serverError } = useAuthentication();

  // Helper untuk mulai/sync timer cooldown dari timestamp
  const startCooldown = useCallback((seconds: number) => {
    const expiresAt = Date.now() + seconds * 1000;
    localStorage.setItem(STORAGE_KEY, expiresAt.toString());
    setTimeLeft(seconds);
    setIsTimerActive(true);
  }, []);

  // 1. Restore timer dari localStorage saat komponen di-mount / di-refresh
  useEffect(() => {
    const storedExpiresAt = localStorage.getItem(STORAGE_KEY);
    if (storedExpiresAt) {
      const remainingSeconds = Math.ceil((parseInt(storedExpiresAt, 10) - Date.now()) / 1000);
      if (remainingSeconds > 0) {
        setTimeLeft(remainingSeconds);
        setIsTimerActive(true);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // 2. Decrement Countdown Timer tiap detik
  useEffect(() => {
    if (!isTimerActive || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsTimerActive(false);
          localStorage.removeItem(STORAGE_KEY);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isTimerActive, timeLeft]);

  const validateEmail = (): boolean => {
    let isValid = true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email.trim()) {
      setEmailError(t("ForgotPassword.error_email_required") || "Masukkan email terlebih dahulu...");
      isValid = false;
    } else if (!emailRegex.test(email)) {
      setEmailError(t("ForgotPassword.error_email_invalid") || "Format email tidak valid");
      isValid = false;
    } else {
      setEmailError(undefined);
    }

    return isValid;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (isTimerActive) {
      toast.error(`Silakan tunggu ${timeLeft} detik lagi.`);
      return;
    }

    if (!validateEmail()) return;

    const result = await forgotPassword({ email });

    if (result.success) {
      toast.success(result.data?.message || t("ForgotPassword.success_toast") || "Reset link sent to your email!");
      // Jalankan Cooldown 30 Detik
      startCooldown(COOLDOWN_SECONDS);
    } else {
      // BILA DITOLAK BACKEND (Termasuk jika dites di Incognito / Spam Request):
      toast.error(result.error || t("ForgotPassword.error_toast") || "Failed to send reset link.");
      
      // Jika Backend memberikan pesan penolakan karena cooldown / token masih aktif, 
      // Kunci kembali tombolnya di FE Incognito ini!
      startCooldown(COOLDOWN_SECONDS);
    }
  };

  const clearError = () => {
    if (emailError) {
      setEmailError(undefined);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-4 font-sans relative">
      
      {/* Wrapper Utama */}
      <div className="w-full max-w-[900px] flex justify-center lg:justify-between items-center lg:items-stretch gap-0 lg:gap-8">
        
        {/* HERO LEFT SIDEBAR */}
        <div className="hidden lg:flex w-[48%] bg-gradient-to-b from-[#F15C61] to-[#f47c80] rounded-[28px] p-8 pb-0 flex-col justify-between relative overflow-hidden min-h-[480px] shadow-[0_20px_50px_rgba(241,92,97,0.2)]"> 
          <div className="relative z-10 mt-1 pr-2 lg:pr-4"> 
            <h1 className="text-white text-[1.75rem] font-bold leading-[1.25] tracking-tight mb-3 text-balance">
              {t("ForgotPassword.hero_title") || "Undangan Digital Profesional No.1 Di Indonesia. Untuk Segala Jenis Acara 100% GRATIS"}
            </h1>
            <p className="text-white/95 text-[0.9rem] leading-relaxed font-medium">
              {t("ForgotPassword.hero_desc") || "Kelola undangan, RSVP, pengingat acara, hingga amplop digital dalam satu platform yang rapi, otomatis, dan gratis tanpa batas."}
            </p>
          </div>

          <div className="relative mt-6 flex-1 w-full flex justify-center items-end">
            <div className="relative w-[290px] aspect-[4/5]">
              <div className="absolute left-1/2 bottom-[5%] -translate-x-1/2 w-[90%] h-[60%] bg-white/40 blur-[70px] rounded-full -z-10 pointer-events-none"></div>
              <Image 
                src="/images/cta/cta.png" 
                alt="Elynk App Mockup" 
                fill 
                className="object-contain object-bottom drop-shadow-[0_20px_40px_rgba(0,0,0,0.15)] relative z-10"
                priority
              />
            </div>
          </div>
        </div>

        {/* KANAN: FORM FORGOT PASSWORD */}
        <div className="w-full max-w-[400px] lg:w-[52%] lg:max-w-[380px] bg-white lg:bg-transparent shadow-sm lg:shadow-none rounded-3xl lg:rounded-none flex flex-col justify-center py-8 lg:py-4 px-6 lg:px-0 lg:pr-2">
          
          <div className="flex flex-col items-center mb-8">
            <Link href="/" className="mb-6 inline-block">
              <Image 
                src="/images/logo/elynk.png"
                alt="Elynk Logo" 
                width={140} 
                height={45} 
                className="object-contain"
              />
            </Link>
            <h2 className="text-[1.8rem] lg:text-[2rem] font-bold text-[#2A2A2A] text-center mb-2 tracking-tight">
              {t("ForgotPassword.form_title") || "Forgot Password?"}
            </h2>
            <p className="text-[0.95rem] text-gray-400 font-normal text-center leading-snug">
              {t("ForgotPassword.form_desc_1") || "Enter your email address below and we will"}<br className="hidden sm:block"/>{t("ForgotPassword.form_desc_2") || "send you a link to reset your password."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col space-y-4" noValidate>
            
            {/* EMAIL INPUT */}
            <div className="flex flex-col space-y-1">
              <div className="relative">
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearError(); }}
                  disabled={isTimerActive} 
                  placeholder={t("ForgotPassword.email_placeholder") || "Email Address"}
                  className={`w-full text-[#2A2A2A] placeholder-gray-400 rounded-[12px] h-[50px] px-4 outline-none transition-all text-[0.95rem] disabled:bg-[#f1f4f9] disabled:text-gray-500
                    ${emailError 
                      ? 'bg-red-50 border border-red-400 focus:ring-2 focus:ring-red-400/30' 
                      : 'bg-[#F5F5F5] border-none focus:ring-2 focus:ring-[#F15C61]/30'}`}
                />
                <AtSymbolIcon className={`absolute right-4 top-1/2 -translate-y-1/2 w-[20px] h-[20px] ${emailError ? 'text-red-400' : 'text-gray-400'}`} strokeWidth={2} />
              </div>
              {emailError && (
                <span className="flex items-center gap-1 text-red-500 text-[0.8rem] font-medium mt-1 ml-1">
                  <ExclamationCircleIcon className="w-4 h-4" /> {emailError}
                </span>
              )}
            </div>

            {/* COUNTDOWN TIMER DISPLAY */}
            {isTimerActive && (
              <div className="flex justify-end items-center px-1 -mt-1">
                <span className="text-[0.85rem] font-bold text-gray-600 transition-colors duration-300">
                  00:{timeLeft.toString().padStart(2, '0')} {t("ForgotPassword.timer_sec") || "Sec"}
                </span>
              </div>
            )}

            {/* SERVER ERROR ALERT */}
            {serverError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-[12px] text-[0.85rem] font-medium flex items-start gap-2">
                <ExclamationCircleIcon className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{serverError}</span>
              </div>
            )}

            {/* SUBMIT BUTTON */}
            <div className="pt-2">
              <button 
                type="submit" 
                disabled={isLoading || isTimerActive}
                className="w-full flex items-center justify-center bg-[#E56A6B] hover:bg-[#D55A5B] disabled:bg-[#E56A6B]/70 disabled:cursor-not-allowed text-white font-semibold text-[1rem] h-[52px] rounded-[12px] transition-colors shadow-sm"
              >
                {isLoading ? (
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                {isLoading 
                  ? (t("ForgotPassword.btn_sending") || "Sending...") 
                  : isTimerActive 
                    ? (t("ForgotPassword.btn_wait") || "Wait before resend...") 
                    : (t("ForgotPassword.btn_send_link") || "Send Reset Link")}
              </button>
            </div>

          </form>

          {/* BACK TO LOGIN LINK */}
          <div className="mt-8 text-center">
            <Link href="/login" className="text-[0.95rem] text-gray-600 hover:text-[#F15C61] transition-colors font-medium flex items-center justify-center gap-2">
              <ArrowLeftIcon className="text-[#F15C61] w-4 h-4" strokeWidth={2.5}/>
              {t("ForgotPassword.back_to") || "Back to"}{" "}
              <span className="font-bold underline">{t("ForgotPassword.login_link") || "Login"}</span>
            </Link>
          </div>

        </div>

      </div>
    </div>
  );
}